import PartnerIcon from 'components/elements/icons/PartnerIcon'
import RequesterIcon from 'components/elements/icons/RequesterIcon'
import { MarkerProps } from 'components/elements/maps/Marker'
import TrackingMap from 'components/elements/maps/TrackingMap'
import ErrorModal from 'components/elements/modal/ErrorModal'
import InformationModal from 'components/elements/modal/InformationModal'
import InlineText from 'components/elements/texts/InlineText'
import Text from 'components/elements/texts/Text'
import { CoordsProps, TrackingProps } from 'constants/map'
import { PAGE_PATH } from 'constants/pagePath'
import { QN_STATUS } from 'constants/qnStatus'
import { TRACKING_MAP_HEIGHT } from 'constants/size'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import dayjs from 'dayjs'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { useWatch } from 'utils/commonHooks'
import { transitionWithAction } from 'utils/commonUtils'

import GlobalHeaderArea from '../RequestTemplate/GlobalHeaderArea'

/**
 * 対象がTrackingAreaProps型かチェックする
 * @param target チェック対象
 */
export const isTrackingAreaProps = (
  target: any
): target is TrackingAreaProps => {
  return target.requester !== undefined && target.partner !== undefined
}

/**
 * パートナーの位置共有用マップ表示エリア
 */
const TrackingArea: React.FC<TrackingAreaProps> = (props) => {
  // マーカーの構築
  const buildMarkers = (
    requester: CoordsProps,
    partner: TrackingProps
  ): Array<MarkerProps> => {
    const result = [
      {
        component: <RequesterIcon />,
        ...requester,
      },
    ]
    if (partner.visible && props.qnStatus === QN_STATUS.DISPATCHING) {
      result.push({
        component: <PartnerIcon />,
        ...partner,
      })
    }
    return result
  }

  const [unstableModal, setUnstableModal] = useState(false)
  const [locationOffModal, setLocationOffModal] = useState(false)
  const [locationOff, setLocationOff] = useState(false)
  const [reassignedModal, setReassignedModal] = useState(false)

  const closeLocationOff = useCallback(() => {
    setLocationOffModal(false)
  }, [])

  const history = useHistory()
  const historyStackContext = useContext(HistoryStackContext)
  const closeReassigned = useCallback(() => {
    transitionWithAction(historyStackContext, '再手配モーダルのOK', () => {
      history.push(PAGE_PATH.RE_ASSIGNED)
    })
  }, [])

  const isLocationSharingTimeout = dayjs(props.lastLocationSendDate)
    .add(props.locationSharingTimeout, 'second')
    .isBefore(dayjs())

  // ステータスやパートナー状況に応じてマップ下の表示を切り替える
  useEffect(() => {
    if (!props.setTrackingContent) {
      return
    }

    const arrivalDate = new Date()
    arrivalDate.setMinutes(arrivalDate.getMinutes() + props.arrivalDuration)

    if (props.partnerStable && !isLocationSharingTimeout) {
      setUnstableModal(false)
    } else {
      setUnstableModal(true)
    }

    if (props.partner.visible) {
      setLocationOffModal(false)
      setLocationOff(false)
    } else {
      // 連続して位置情報OFFを受信した場合は、位置情報OFFモーダルを表示しない
      if (!locationOff) {
        setLocationOffModal(true)
        setLocationOff(true)
      }
    }

    if (props.qnStatus === QN_STATUS.REASSIGNED) {
      setReassignedModal(true)
    } else {
      setReassignedModal(false)
    }

    props.setTrackingContent(
      <React.Fragment>
        {props.qnStatus === QN_STATUS.COMING_SOON ||
        props.arrivalDuration <= props.timeComingSoon ? (
          <ComingSoonContainer>
            <Text bold>もうすぐ到着予定です。</Text>
            <Text bold>お車付近でお待ちください。</Text>
          </ComingSoonContainer>
        ) : (
          <React.Fragment>
            <ArrivalContainer>
              <MinuteContainer>
                {props.partner.visible && (
                  <React.Fragment>
                    {props.arrivalDuration !== 0 ? (
                      <React.Fragment>
                        <MinuteText fontSize="40px">
                          {props.arrivalDuration}
                        </MinuteText>
                        <InlineText fontSize="26px">分後</InlineText>
                      </React.Fragment>
                    ) : (
                      <InlineText fontSize="26px">
                        所要時間を計算中...
                      </InlineText>
                    )}
                  </React.Fragment>
                )}
              </MinuteContainer>
              {props.arrivalDuration !== 0 && (
                <Text>
                  {`${arrivalDate.getHours()}:${(
                    '0' + arrivalDate.getMinutes()
                  ).slice(-2)}`}
                  到着予定
                </Text>
              )}
            </ArrivalContainer>
            <Divider />
            <MessageContainer>
              <Text fontSize="15px">
                {`到着予定時間は最短ルートによる目安です。対応する車両が通行できるルートや道路状況によって前後しますのでご了承ください。\n${props.partnerName}のスタッフが向かいます。スタッフ到着時はお車付近でお待ちください。到着予定の約5分前にSMSで通知します。`}
              </Text>
            </MessageContainer>
          </React.Fragment>
        )}
        <ErrorModal
          on={unstableModal}
          message={
            'スタッフの位置情報が不安定です。\n画面を更新するか、SMSでお送りしたURLを再度開いてみてください。'
          }
        />
        <InformationModal
          on={locationOffModal}
          content={
            '現場へ向かうスタッフの位置情報が取得できません。\n現場には向かっているのでご安心ください。'
          }
          closeLabel="OK"
          closeFunction={closeLocationOff}
          top={'80px'}
        />
        <InformationModal
          on={reassignedModal}
          content={'情報を再取得します'}
          closeLabel="OK"
          closeFunction={closeReassigned}
          top={'160px'}
        />
      </React.Fragment>
    )
  }, [
    props.partner,
    props.partnerStable,
    props.partnerName,
    props.qnStatus,
    props.arrivalDuration,
    props.timeComingSoon,
    props.setTrackingContent,
    unstableModal,
    locationOffModal,
    reassignedModal,
  ])

  // 最後に妥当な位置情報を受信してから一定時間経過経過後、不安定表示になる
  const counter = useWatch(1000)
  useEffect(() => {
    if (isLocationSharingTimeout) {
      setUnstableModal(true)
    }
  }, [counter])

  return (
    <React.Fragment>
      <GlobalHeaderArea />
      <TrackingMap
        height={TRACKING_MAP_HEIGHT}
        defaultCenter={{ ...props.requester }}
        markers={buildMarkers(props.requester, props.partner)}
        fullscreenControl
      />
    </React.Fragment>
  )
}

export type TrackingAreaProps = {
  requester: CoordsProps
  partner: TrackingProps
  partnerStable: boolean
  qnStatus: string
  partnerName: string
  arrivalDuration: number
  timeComingSoon: number
  lastLocationSendDate: string
  locationSharingTimeout: number
  setTrackingContent?: React.Dispatch<React.SetStateAction<React.ReactNode>>
}

const ComingSoonContainer = styled.div`
  margin: 0 24px 12px;
  text-align: center;
`

const ArrivalContainer = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  height: 48px;
  margin: 0 24px 4px;
`

const MinuteContainer = styled.div`
  position: relative;
  top: 4px;
`

const MinuteText = styled(InlineText)`
  position: relative;
  top: 4px;
  font-size: 40px;
`

const Divider = styled.div`
  height: 3px;
  margin: 0 24px 8px;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 40px;
`

const MessageContainer = styled.div`
  margin: 0 24px 8px;
`

export default TrackingArea
